import { convertToTimezone, convertToUtc } from "@gigpro/utils/datetime";
import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import { produce } from "immer";
import {
  hireScreeningInterviewsCreate,
  hireScreeningInterviewsPartialUpdate,
  useHireInterviewsHiringScheduleList,
  useHireScreeningInterviewsRetrieve,
} from "../../generated/hire/hire";
import type { HiringScheduleItem } from "../../generated/model/hiringScheduleItem";
import type { PatchedScreeningInterviewRescheduleRequest } from "../../generated/model/patchedScreeningInterviewRescheduleRequest";
import type { ScreeningInterviewCreateRequest } from "../../generated/model/screeningInterviewCreateRequest";
import type { ScreeningInterviewManager } from "../../generated/model/screeningInterviewManager";
import { composeSelect, type Tz } from "../types";

interface ScreeningInterviewCreateRequestWithTransform extends ScreeningInterviewCreateRequest, Tz {}
interface ScreeningInterviewRescheduleRequestWithTransform extends PatchedScreeningInterviewRescheduleRequest, Tz {}

/** Reinterpret the location-local `start_time` as the UTC instant via the `timezone` mixin, then drop the
 *  mixin so only wire fields ship. Shared by the create + reschedule write wrappers. */
const applyTzToScreeningInterview = <T extends Tz & { start_time?: Date | null }>(request: T): T =>
  produce(request, (draft) => {
    if (draft.timezone && draft.start_time) draft.start_time = convertToUtc(draft.start_time, draft.timezone);
    delete draft.timezone;
  });

type ScreeningInterviewCreateWithTransformVariables = { data: ScreeningInterviewCreateRequestWithTransform };

export function useHireScreeningInterviewsCreateWithTransform(options?: {
  mutation?: Omit<
    UseMutationOptions<ScreeningInterviewManager, unknown, ScreeningInterviewCreateWithTransformVariables>,
    "mutationFn"
  >;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: ({ data }: ScreeningInterviewCreateWithTransformVariables) =>
      hireScreeningInterviewsCreate(applyTzToScreeningInterview(data)),
  });
}

type ScreeningInterviewPartialUpdateWithTransformVariables = {
  id: string;
  data: ScreeningInterviewRescheduleRequestWithTransform;
};

export function useHireScreeningInterviewsPartialUpdateWithTransform(options?: {
  mutation?: Omit<
    UseMutationOptions<ScreeningInterviewManager, unknown, ScreeningInterviewPartialUpdateWithTransformVariables>,
    "mutationFn"
  >;
}) {
  return useMutation({
    ...options?.mutation,
    mutationFn: ({ id, data }: ScreeningInterviewPartialUpdateWithTransformVariables) =>
      hireScreeningInterviewsPartialUpdate(id, applyTzToScreeningInterview(data)),
  });
}

// Zone shift times into the posting's location tz. Nullable tz falls back to UTC (matches the grid's label).
// Works across both feed variants (working `Interview` + `ScreeningInterview`) — all three fields it
// touches are shared by the `HiringScheduleItem` union members.
export const applyTzToInterview = produce<HiringScheduleItem>((draft) => {
  const timezone = draft.timezone ?? "UTC";
  draft.start_time = convertToTimezone(draft.start_time, timezone);
  draft.end_time = convertToTimezone(draft.end_time, timezone);
});

export function useHireInterviewsHiringScheduleListWithTransform<TData = Array<HiringScheduleItem>>(
  ...[params, options]: Parameters<typeof useHireInterviewsHiringScheduleList<TData>>
) {
  return useHireInterviewsHiringScheduleList<TData>(params, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect((data: Array<HiringScheduleItem>) => data.map(applyTzToInterview), options?.query?.select),
    },
  });
}

const applyTzToScreeningInterviewRead = produce<ScreeningInterviewManager>((draft) => {
  draft.start_time = convertToTimezone(draft.start_time, draft.timezone);
  draft.end_time = convertToTimezone(draft.end_time, draft.timezone);
});

export function useHireScreeningInterviewsRetrieveWithTransform<TData = ScreeningInterviewManager>(
  ...[id, options]: Parameters<typeof useHireScreeningInterviewsRetrieve<TData>>
) {
  return useHireScreeningInterviewsRetrieve<TData>(id, {
    ...options,
    query: {
      ...options?.query,
      select: composeSelect(applyTzToScreeningInterviewRead, options?.query?.select),
    },
  });
}
